// pothos manager «QA» v2 (хэндофф 2026-07-19) — /api/getManagerQaBoard +
// /api/setAssetVerdict. Очередь = строки каталога pothos_assets в статусе
// 'qa', разложенные по источникам (dept_photographer / dept_upwork /
// dept_scrapper / dept_it). ai-блок кадра — из check_comment (зеркалится
// work-evalом; у исторических кадров может быть пуст). Вердикты acc/rej
// для work-кадров идут через work-api reviewFrame (деньги earner'a там),
// setAssetVerdict — для остальных строк и для пометок question/recheck.
import { z } from 'zod';

export const PothosQaFrameSchema = z.object({
  id: z.string(),
  source: z.enum(['photo', 'upwork', 'scrape', 'it']),
  source_pipeline: z.string(),
  author: z.string(),
  merchant: z.string(),
  country: z.string(),
  country_iso: z.string(),
  locale: z.string(),
  category: z.string(),
  modality: z.string(),
  group: z.string().default(''),
  pos: z.number(),
  of: z.number(),
  ai_note: z.string(),
  uploaded_at: z.string(),
  marked: z.string(),
  thumb_url: z.string(),
  full_url: z.string(),
  // Цифровой исходник материала (рендер/скрин с тем же уникальным кодом);
  // '' = исходник не залит — кнопка «Исходник» в ревью неактивна.
  source_url: z.string().default(''),
  // Устройство съёмки из индекса ('' = не определено); правится из ревью
  // через setAssetMeta (вокабуляр: iphone | other).
  device: z.string().default(''),
  // Организатор события (билеты с манифест-сверкой; юнит пачки «20 на
  // организатора» у агрегаторов) и название события — для группового
  // отклонения rejectQaGroup.
  organizer: z.string().default(''),
  event_name: z.string().default(''),
  // Держатель/участник (печать: holder_name страницы) — для «Удалить
  // участника» (rejectPrintParticipant). '' = не применимо.
  holder_name: z.string().default(''),
  // Происхождение отснятого кадра: какая страница какой печатной пачки на
  // нём снята (мигр. pothos/0097). '' / 0 = кадр не из печати.
  print_batch: z.string().default(''),
  print_page_no: z.number().default(0),
});
export type PothosQaFrame = z.infer<typeof PothosQaFrameSchema>;

// Полные счётчики очереди по источникам (агрегат по ВСЕМУ индексу, не по
// подгруженной странице кадров): especially после массовых импортов свежие
// кадры одного источника вытесняли остальные из страницы, и разбивка
// показывала нули при непустой очереди.
export const PothosQaSourceTotalsSchema = z.object({
  photo: z.number().default(0),
  upwork: z.number().default(0),
  scrape: z.number().default(0),
  it: z.number().default(0),
});
export type PothosQaSourceTotals = z.infer<typeof PothosQaSourceTotalsSchema>;

// Полная разбивка очереди по (источник, автор) — агрегат по всему индексу,
// чтобы в раскрытом источнике были видны ВСЕ люди с честными счётчиками, а не
// только авторы попавших в подгрузку кадров. Кадры конкретного автора
// догружаются тем же эндпоинтом с query source+author (frames в ответе тогда
// только этого автора).
export const PothosQaAuthorTotalSchema = z.object({
  source: z.enum(['photo', 'upwork', 'scrape', 'it']),
  author: z.string(),
  n: z.number(),
});
export type PothosQaAuthorTotal = z.infer<typeof PothosQaAuthorTotalSchema>;

// Разбивка очереди по площадкам (мерчант × страна, сырые значения индекса —
// country может нести подчёркивания) — для точечной проверки пачки одной
// площадки. Кадры площадки грузятся тем же эндпоинтом с query
// source+merchant+country.
export const PothosQaPlatformTotalSchema = z.object({
  source: z.enum(['photo', 'upwork', 'scrape', 'it']),
  merchant: z.string(),
  country: z.string(),
  n: z.number(),
});
export type PothosQaPlatformTotal = z.infer<typeof PothosQaPlatformTotalSchema>;

export const PothosManagerQaBoardResSchema = z.object({
  frames: z.array(PothosQaFrameSchema),
  it_screens: z.array(PothosQaFrameSchema),
  queue_total: z.number().default(0),
  source_totals: PothosQaSourceTotalsSchema.default({ photo: 0, upwork: 0, scrape: 0, it: 0 }),
  author_totals: z.array(PothosQaAuthorTotalSchema).default([]),
  platform_totals: z.array(PothosQaPlatformTotalSchema).default([]),
  // «Ревизия» — кадры с меткой «доп. проверка»: исключены из всех общих
  // очередей/счётчиков и отдаются ТОЛЬКО ревизору (QA_REVISION_EMAIL,
  // секрет воркера); остальным приходит пустой список и ноль.
  revision_frames: z.array(PothosQaFrameSchema).default([]),
  revision_total: z.number().default(0),
  // «Одобрено · по проверяющим» — счётчики status='accepted' (готово к
  // отгрузке, не отгружено) по dept_qa. Только ревизору; кадры конкретного
  // проверяющего грузятся этим же эндпоинтом с query approved_by=<email>.
  approved_totals: z.array(z.object({ reviewer: z.string(), n: z.number() })).default([]),
});
export type PothosManagerQaBoardRes = z.infer<typeof PothosManagerQaBoardResSchema>;

// POST /api/setAssetModality — ручная правка типа съёмки прямо из QA-ревью
// (photo | screenshot | photo_screenshot — третья = ФОТО экрана, встречается
// у индонезийских купонов приложений; вокабуляр старых трекеров).
export const PothosSetAssetModalityReqSchema = z.object({
  asset_id: z.string().trim().min(1).max(64),
  modality: z.enum(['photo', 'screenshot', 'photo_screenshot']),
});
export type PothosSetAssetModalityReq = z.infer<typeof PothosSetAssetModalityReqSchema>;
export const PothosSetAssetModalityResSchema = z.object({ ok: z.boolean() });

// POST /api/setAssetMeta — ручная правка атрибутов кадра из QA-ревью
// (любой статус строки, как и setAssetModality): категория, устройство
// съёмки, тип съёмки. Все поля опциональны — обновляются только переданные.
export const PothosSetAssetMetaReqSchema = z.object({
  asset_id: z.string().trim().min(1).max(64),
  category: z.enum(['coupon', 'gift_card', 'membership', 'event_ticket']).optional(),
  device: z.enum(['iphone', 'other']).optional(),
  modality: z.enum(['photo', 'screenshot', 'photo_screenshot']).optional(),
});
export type PothosSetAssetMetaReq = z.infer<typeof PothosSetAssetMetaReqSchema>;
export const PothosSetAssetMetaResSchema = z.object({ ok: z.boolean() });

// POST /api/rejectQaGroup — «Отклонить мерчанта целиком»: снимает с QA всю
// пачку одной группы (qa + уже принятые в этой сессии). Юнит группы: у
// агрегаторов билетов — организатор (или событие, если организатора нет в
// манифесте), у обычных мерчантов — мерчант×страна внутри категории.
// merchant/country/organizer/event_name передаются СЫРЫМИ значениями индекса.
export const PothosRejectQaGroupReqSchema = z.object({
  category: z.string().trim().min(1).max(40),
  merchant: z.string().max(200),
  country: z.string().max(100),
  organizer: z.string().max(300).default(''),
  event_name: z.string().max(300).default(''),
});
export type PothosRejectQaGroupReq = z.infer<typeof PothosRejectQaGroupReqSchema>;
export const PothosRejectQaGroupResSchema = z.object({ ok: z.boolean(), rejected: z.number() });

// POST /api/replaceAssetImage — «переворот кадра» из ревью: клиент вращает
// картинку канвасом и присылает новые байты (base64 JPEG, full + thumb);
// воркер перезаписывает ОБА объекта в R2 и обновляет width/height/size/sha
// в индексе. op — для аудита, какая операция была применена.
export const PothosReplaceAssetImageReqSchema = z.object({
  asset_id: z.string().trim().min(1).max(64),
  op: z.enum(['rot90', 'rot180', 'rot270', 'flip', 'crop']),
  full_b64: z.string().min(100),
  thumb_b64: z.string().min(100),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type PothosReplaceAssetImageReq = z.infer<typeof PothosReplaceAssetImageReqSchema>;
export const PothosReplaceAssetImageResSchema = z.object({ ok: z.boolean() });

// POST /api/aiRecheckAsset — кнопка «Не понимаю что это» в ревью: кадр
// уходит vision-модели (ENRICH_MODEL, Gemma 4) на переклассификацию типа
// (купон/гифт/мембершип/билет) с краткой русской заметкой; воркер применяет
// категорию/мерчанта и ставит метку «доп. проверка» — кадр уезжает в
// приватную группу «Ревизия».
export const PothosAiRecheckReqSchema = z.object({
  asset_id: z.string().trim().min(1).max(64),
});
export type PothosAiRecheckReq = z.infer<typeof PothosAiRecheckReqSchema>;
export const PothosAiRecheckResSchema = z.object({
  ok: z.boolean(),
  category: z.string().default(''),
  merchant: z.string().default(''),
  note: z.string().default(''),
});

export const PothosSetAssetVerdictReqSchema = z.object({
  asset_id: z.string().trim().min(1).max(64),
  verdict: z.enum(['acc', 'rej', 'question', 'recheck']),
  comment: z.string().max(500).default(''),
});
export type PothosSetAssetVerdictReq = z.infer<typeof PothosSetAssetVerdictReqSchema>;
export const PothosSetAssetVerdictResSchema = z.object({ ok: z.boolean() });
